import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { supabase } from '../lib/supabase';
import { ArrowLeft, Edit2 } from 'lucide-react';
import { NoteEditor } from '../components/BrainDump/NoteEditor';
import type { Note } from '../hooks/useNotes';

export function BrainDumpView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [note, setNote] = useState<Note | null>(null);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);

  useEffect(() => {
    fetchNote();
  }, [id]);

  const fetchNote = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notes')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setNote(data);
    } catch (err) {
      console.error('Error fetching note:', err);
      navigate('/brain-dump');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (noteData: { 
    content: Note['content']; 
    tags: string[]; 
    color: string;
    folderId?: string;
  }) => {
    if (!note) return;

    try {
      const { error } = await supabase
        .from('notes')
        .update({
          content: noteData.content,
          color: noteData.color,
          folder_id: noteData.folderId
        })
        .eq('id', note.id);

      if (error) throw error;
      await fetchNote();
      setShowEditor(false);
    } catch (err) {
      console.error('Error updating note:', err);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate('/brain-dump')}
            className="flex items-center gap-2 px-4 py-2 bg-black/20 rounded-xl hover:bg-black/30 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back to Brain Dump</span>
          </button>
          {note && (
            <button
              onClick={() => setShowEditor(true)}
              className="flex items-center gap-2 px-4 py-2 bg-[#c8e45c] text-black rounded-xl hover:opacity-90 transition-opacity"
            >
              <Edit2 className="w-4 h-4" />
              <span>Edit Note</span>
            </button>
          )}
        </div>

        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : note ? (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h1 className="text-3xl">{note.content.title}</h1>
            </div>
            <div 
              className="p-6 rounded-xl whitespace-pre-wrap break-words"
              style={{ backgroundColor: note.color || '#2A2B2E' }}
            >
              {note.content.text}
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-400">
            Brain Dump not found
          </div>
        )}
      </div>

      {showEditor && note && (
        <NoteEditor
          initialContent={note.content}
          initialTags={note.tags}
          initialColor={note.color}
          initialFolderId={note.folderId}
          folders={[]} // TODO: Pass folders from context
          onSave={handleSave}
          onClose={() => setShowEditor(false)}
        />
      )}
    </Layout>
  );
}